<template>
	<div>
		<!-- Connection type: API (recommended) or legacy FTP. Choosing API shows the
		     service-account form and skips the FTP store-details + SFTP steps. -->
		<div class="tw-mb-5 tw-inline-flex tw-rounded-full tw-border tw-border-gray-200 tw-bg-gray-100 tw-p-1">
			<button
				type="button"
				@click="mode = 'api'"
				class="tw-rounded-full tw-px-5 tw-py-2 tw-text-sm tw-font-medium tw-transition"
				:class="mode === 'api' ? 'tw-bg-brand-500 tw-text-white tw-shadow' : 'tw-text-gray-600 hover:tw-text-gray-800'"
			>
				{{ $t('integrations.noon-connect-via-api') }}
			</button>
			<button
				type="button"
				@click="mode = 'ftp'"
				class="tw-rounded-full tw-px-5 tw-py-2 tw-text-sm tw-font-medium tw-transition"
				:class="mode === 'ftp' ? 'tw-bg-brand-500 tw-text-white tw-shadow' : 'tw-text-gray-600 hover:tw-text-gray-800'"
			>
				{{ $t('integrations.noon-connect-via-ftp') }}
			</button>
		</div>

		<ConfigureApiConnection v-if="mode === 'api'" />

		<div v-else>
		<div class="tw-text-3xl tw-font-bold" v-if="!isDone && !loading">
			{{ $t('integrations.enter-noon-details-title') }}
		</div>
		<div class="tw-flex tw-items-center tw-gap-2" v-else>
			<div class="tw-text-3xl tw-font-bold">
				{{ $t('integrations.thanks-for-submit-details') }}
			</div>
			<div
				class="'tw-border-secondary-500 tw-z-10 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-border tw-bg-secondary-500 tw-text-white"
				style="width: 32px; height: 32px"
			>
				<AppIcon name="check" />
			</div>
		</div>
		<div class="tw-font-medium tw-text-gray-500" v-if="!isDone && !loading">
			{{ $t('integrations.enter-noon-details-description') }}
		</div>
		<div class="tw-font-medium tw-text-gray-500" v-else>
			{{ $t('integrations.thanks-for-submit-details-description') }}
		</div>
		<AppForm
			@submit="onSubmit"
			class="tw-mt-3 tw-flex tw-flex-col tw-gap-2"
			v-if="!isDone && !loading"
			style="width: 90%"
		>
			<div class="tw-grid tw-grid-cols-2 tw-gap-4">
				<AppInput
					v-model="form.noon_email"
					placeholder="someone@example.com"
					:label="$t('integrations.noon-account-email')"
					:rules="['required', 'isEmail']"
					:disabled="loading"
				/>

				<AppInput
					v-model="form.store_name"
					:placeholder="$t('integrations.enter-store-name')"
					:label="$t('ziebart.store-name')"
					:rules="['required']"
					:disabled="loading"
				/>
			</div>

			<div class="tw-grid tw-grid-cols-2 tw-gap-4">
				<AppInput
					v-model="form.partner_id"
					:placeholder="$t('integrations.enter-noon-project-id')"
					:label="$t('integrations.project-id')"
					:rules="['required']"
					:disabled="loading"
				>
					<template #hint>
						<div
							class="tw-cursor-pointer tw-text-gray-600 tw-underline"
							@click="openHelpDialog('project-id')"
						>
							{{ $t('integrations.how-to-find-noon-project-id') }}
						</div>
					</template>
				</AppInput>

				<AppInput
					v-model="form.warehouse_code"
					:placeholder="$t('integrations.enter-noon-warehouse-code')"
					:label="$t('integrations.warehouse-code')"
					:rules="['required']"
					:disabled="loading"
				>
					<template #hint>
						<div
							class="tw-cursor-pointer tw-text-gray-600 tw-underline"
							@click="openHelpDialog('warehouse-code')"
						>
							{{ $t('integrations.how-to-find-noon-warehouse-code') }}
						</div>
					</template>
				</AppInput>
			</div>

			<div class="tw-mt-5">
				<AppButton
					:label="$t('integrations.submit-details')"
					size="xl"
					class="tw-rounded-full !tw-px-6"
					type="submit"
					:disabled="loading"
				/>
			</div>
		</AppForm>
		<div class="tw-mt-5 tw-flex tw-items-center tw-gap-3" v-else>
			<AppButton
				v-if="loading"
				:label="$t('integrations.pending-connection')"
				size="xl"
				class="tw-rounded-full"
				type="button"
				disabled
			/>
			<AppButton
				v-if="isDone"
				:label="$t('integrations.next-step')"
				size="xl"
				class="tw-rounded-full"
				type="button"
				endIcon="assets/v2/image/icons/arrow-narrow-right.svg"
				@click="nextStep"
				:disabled="nextStepLoading"
			/>
		</div>
		<HelpDialogProjectId ref="helpDialogProjectIdRef" />
		<HelpDialogWarehouseCode ref="helpDialogWarehouseCodeRef" />
		</div>
	</div>
</template>

<script setup>
import { ref } from 'vue';
import { useLinkMarketplaceStore } from '../../../../stores/link-marketplace';
import HelpDialogProjectId from './help-dialog-project-id.vue';
import HelpDialogWarehouseCode from './help-dialog-warehouse-code.vue';
import ConfigureApiConnection from './configure-api-connection.vue';
import { Marketplace } from '../../../../entities/marketplace';
import { marketplaceStepStatusEnum } from '../../helper';

const linkMarketplaceStore = useLinkMarketplaceStore();

// Default to the API flow; users with a legacy FTP setup can switch to 'ftp'.
const mode = ref('api');

const form = ref({
	noon_email: '',
	store_name: '',
	partner_id: '',
	warehouse_code: '',
});

const helpDialogProjectIdRef = ref(null);
const helpDialogWarehouseCodeRef = ref(null);

const loading = ref(
	linkMarketplaceStore.currentStepData.status === marketplaceStepStatusEnum.InProgress,
);
const isDone = ref(
	linkMarketplaceStore.currentStepData.status === marketplaceStepStatusEnum.Complete,
);

const onSubmit = async () => {
	loading.value = true;
	try {
		const success = await Marketplace.setNoonDetails(
			linkMarketplaceStore.linkedMarketplaceId,
			form.value,
		);
		if (!success) {
			document.location.href = '/store/subscribe/renew_package';
		}
	} catch (err) {
		loading.value = false;
	}
	// await linkMarketplaceStore.refetchSteps();
};

const openHelpDialog = (dialogName) => {
	if (dialogName === 'project-id') {
		helpDialogProjectIdRef.value.open();
	} else {
		helpDialogWarehouseCodeRef.value.open();
	}
};

const nextStepLoading = ref(false);
const nextStep = async () => {
	nextStepLoading.value = true;
	try {
		await Marketplace.finishWizardStep(linkMarketplaceStore.currentStepData.id);
		await linkMarketplaceStore.refetchSteps();
	} finally {
		nextStepLoading.value = false;
	}
};
</script>

<style lang="scss" scoped></style>
